import React, { ReactElement, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import useAddToCart, { AddToCartButtonState } from './useAddToCart';

import { widgetActivityTracking } from '../../store/actions';
import { trackingEvents } from '../../store/activityTrackingHelpers';
import Translation from '../Translation';
import { NewSkinLoader, SuccessCheck } from '../svgIcons/SvgIcons';
import styles from './AddToCartButton.styles';

interface AddToCartButtonViewProps {
    buttonState: AddToCartButtonState;
    onAddToCartClick: () => void;
    onViewCartClick: () => void;
    disabled?: boolean;
}

export const AddToCartButtonView = ({
    buttonState,
    onAddToCartClick,
    onViewCartClick,
    disabled = false,
}: AddToCartButtonViewProps): ReactElement | null => {
    if (buttonState === 'add') {
        return (
            <button
                type="button"
                disabled={disabled}
                onClick={onAddToCartClick}
                css={styles.primaryButton}
            >
                <Translation value="recommendProductAddToCart" fallback="Add to cart" />
            </button>
        );
    }
    if (buttonState === 'adding') {
        return (
            <button type="button" disabled css={styles.addingToCartButton}>
                <NewSkinLoader width={20} height={20} />

                <Translation value="recommendProductAddingToCart" fallback="Adding to cart" />
            </button>
        );
    }

    if (buttonState === 'added') {
        return (
            <button type="button" disabled css={styles.addedToCartButton}>
                <SuccessCheck />
                <Translation value="recommendProductAdded" fallback="Added" />
            </button>
        );
    }

    if (buttonState === 'cart') {
        return (
            <button
                type="button"
                disabled={disabled}
                onClick={onViewCartClick}
                css={styles.primaryButton}
            >
                <Translation value="recommendProductViewCart" fallback="View cart" />
            </button>
        );
    }

    return null;
};

interface AddToCartButtonProps {
    displayedVariantId: number;
    messageId: string;
    disabled?: boolean;
    onAddToCart: () => Promise<boolean>;
    isFlyMessage?: boolean;
}

const AddToCartButton = ({
    displayedVariantId,
    messageId,
    disabled,
    onAddToCart,
    isFlyMessage,
}: AddToCartButtonProps): ReactElement | null => {
    const dispatch = useDispatch();

    const onViewCartClick = useCallback(() => {
        dispatch(widgetActivityTracking(trackingEvents.productRecommendationViewCartClicked));
    }, [dispatch]);

    const { buttonState, executeAddToCart, handleViewCart } = useAddToCart({
        displayedVariantId,
        messageId,
        isFlyMessage,
        onAddToCart,
        onViewCartClick,
    });

    const handleAddToCartClick = useCallback(() => {
        executeAddToCart(displayedVariantId);
    }, [executeAddToCart, displayedVariantId]);

    return (
        <AddToCartButtonView
            buttonState={buttonState}
            onAddToCartClick={handleAddToCartClick}
            onViewCartClick={handleViewCart}
            disabled={disabled}
        />
    );
};

export default AddToCartButton;
